import { supabase } from '../lib/supabaseClient';
import { ApiErrorConCodigo } from '../lib/apiErrors';
import type {
  CierreMercado, FichaMercado, FichaMerJson, FichaModJson, FichaModerno,
} from '../domain/pg/tipos';

/**
 * La lectura del levantamiento de **P&G**: mercados (`mer_fichas`, `mer_cierres`) y canal moderno
 * (`pg_fichas`).
 *
 * ⚠️ **No es el estudio de SOFTYS.** Ese es `api/levantamiento.ts`, sus tablas empiezan con
 * `levantamiento_` y está en pausa desde agosto. Los dos se llaman «levantamiento» y esa es
 * exactamente la confusión que el 15-set hizo que un agente contestara sobre el estudio
 * equivocado con total seguridad.
 *
 * ## Quién puede leer esto
 *
 * Las tres tablas tienen RLS que exige `has_role('admin')` o `has_role('datos')`. A cualquier otro
 * rol la consulta le devuelve cero filas **sin error**, que es la peor forma de fallar: la pantalla
 * se dibuja entera y vacía, y se lee como «no hay data». Por eso la matriz de permisos solo le
 * ofrece la pantalla a esos dos roles.
 */

export class PgApiError extends ApiErrorConCodigo {
  constructor(message: string, code: string | null = null) {
    super(message, code, 'PgApiError');
  }
}

/**
 * PostgREST corta en 1000 filas **sin avisar**, así que se pagina y se compara contra el total.
 *
 * La comparación no es paranoia: una lectura corta en silencio produce un resumen completo,
 * coherente y equivocado. Mejor un error que un número que nadie puede sospechar.
 */
const TANDA = 1000;

/** Se exporta para que el levantamiento de Yichang (`api/yichang.ts`) pagine con la MISMA regla. */
export async function traerTodo<T>(
  tabla: string,
  columnas: string,
  arma: (q: ReturnType<ReturnType<typeof supabase.from>['select']>) => unknown,
): Promise<T[]> {
  const filas: T[] = [];
  let desde = 0;
  let total: number | null = null;
  for (;;) {
    const base = supabase.from(tabla).select(columnas, { count: 'exact' });
    const q = arma(base) as typeof base;
    const { data, error, count } = await q.range(desde, desde + TANDA - 1);
    if (error) throw new PgApiError(error.message, error.code ?? null);
    if (count !== null && count !== undefined) total = count;
    filas.push(...((data ?? []) as unknown as T[]));
    if (data === null || data.length < TANDA) break;
    desde += TANDA;
  }
  if (total !== null && filas.length !== total) {
    throw new PgApiError(
      `Lectura incompleta de ${tabla}: llegaron ${filas.length} de ${total} filas. `
      + 'El resumen sería falso, así que no se muestra ninguno.',
    );
  }
  return filas;
}

const texto = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
const numero = (v: unknown): number | null => (typeof v === 'number' ? v : null);
const lista = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x) => typeof x === 'string') : []);

// ═══════════════════════════════════════════════════════════════════════════
// Mercados · canal tradicional
// ═══════════════════════════════════════════════════════════════════════════

const COLUMNAS_MER = 'id, supervisor, mercado_nombre, mercado_direccion, zona, distrito, '
  + 'puesto_numero, puesto_nombre, levantado, motivo_no, fecha, ocurrido_en, registrado_en, '
  + 'lat, lng, accuracy, ficha, fotos, flags, origin, duracion_seg';

export async function listFichasMercado(desde: string, hasta: string): Promise<FichaMercado[]> {
  const crudas = await traerTodo<Record<string, unknown>>(
    'mer_fichas', COLUMNAS_MER,
    // Se ordena por fecha y después por hora: `ocurrido_en` puede venir en NULL en una ficha vieja,
    // y ordenando solo por él esas se irían al fondo aunque sean del día que se está mirando.
    (q) => q.gte('fecha', desde).lte('fecha', hasta)
      .order('fecha', { ascending: false })
      .order('ocurrido_en', { ascending: false, nullsFirst: false }),
  );
  return crudas.map((r) => ({
    id: String(r.id),
    supervisor: texto(r.supervisor),
    mercadoNombre: texto(r.mercado_nombre),
    mercadoDireccion: texto(r.mercado_direccion),
    zona: texto(r.zona),
    distrito: texto(r.distrito),
    puestoNumero: numero(r.puesto_numero),
    puestoNombre: texto(r.puesto_nombre),
    // `levantado` sin valor se trata como NO levantado: contar un puesto como medido porque falta
    // el dato infla la cobertura, que es el número que decide si la ruta se repite.
    levantado: r.levantado === true,
    motivoNo: texto(r.motivo_no),
    fecha: String(r.fecha),
    ocurridoEn: texto(r.ocurrido_en),
    registradoEn: texto(r.registrado_en),
    lat: numero(r.lat),
    lng: numero(r.lng),
    accuracy: numero(r.accuracy),
    ficha: (r.ficha ?? {}) as FichaMerJson,
    fotos: lista(r.fotos),
    flags: lista(r.flags),
    origin: texto(r.origin),
    duracionSeg: numero(r.duracion_seg),
  }));
}

export async function listCierresMercado(desde: string, hasta: string): Promise<CierreMercado[]> {
  const crudas = await traerTodo<Record<string, unknown>>(
    'mer_cierres',
    'id, supervisor, mercado_nombre, fecha, afluencia, actividad_adicional, factor_externo, '
    + 'competencia_activa, observacion',
    (q) => q.gte('fecha', desde).lte('fecha', hasta).order('fecha', { ascending: false }),
  );
  return crudas.map((r) => ({
    id: String(r.id),
    supervisor: texto(r.supervisor),
    mercadoNombre: texto(r.mercado_nombre),
    fecha: String(r.fecha),
    afluencia: texto(r.afluencia),
    actividadAdicional: typeof r.actividad_adicional === 'boolean' ? r.actividad_adicional : null,
    factorExterno: texto(r.factor_externo),
    competenciaActiva: texto(r.competencia_activa),
    observacion: texto(r.observacion),
  }));
}

// ═══════════════════════════════════════════════════════════════════════════
// Canal moderno · góndola
// ═══════════════════════════════════════════════════════════════════════════

/**
 * El embed de `pdv` es necesario, no decorativo: sin él la columna «Tienda» solo tendría un uuid.
 * La RLS de `pdv` incluye `has_role('datos')`, así que no rebota para ninguno de los dos roles que
 * ven esta pantalla.
 */
const COLUMNAS_MOD = 'id, supervisor, pdv_id, pdv:pdv(name), tienda_texto, cadena, fecha, '
  + 'ocurrido_en, registrado_en, lat, lng, accuracy, ficha, fotos, flags, origin';

export async function listFichasModerno(desde: string, hasta: string): Promise<FichaModerno[]> {
  const crudas = await traerTodo<Record<string, unknown>>(
    'pg_fichas', COLUMNAS_MOD,
    (q) => q.gte('fecha', desde).lte('fecha', hasta)
      .order('fecha', { ascending: false })
      .order('ocurrido_en', { ascending: false, nullsFirst: false }),
  );
  return crudas.map((r) => {
    const pdv = r.pdv as { name?: unknown } | null;
    return {
      id: String(r.id),
      supervisor: texto(r.supervisor),
      pdvId: texto(r.pdv_id),
      pdvNombre: pdv == null ? null : texto(pdv.name),
      tiendaTexto: texto(r.tienda_texto),
      cadena: texto(r.cadena),
      fecha: String(r.fecha),
      ocurridoEn: texto(r.ocurrido_en),
      registradoEn: texto(r.registrado_en),
      lat: numero(r.lat),
      lng: numero(r.lng),
      accuracy: numero(r.accuracy),
      ficha: (r.ficha ?? {}) as FichaModJson,
      fotos: lista(r.fotos),
      flags: lista(r.flags),
      origin: texto(r.origin),
    };
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// El borde del rango
// ═══════════════════════════════════════════════════════════════════════════

/**
 * La fecha más vieja y la más nueva que existen, **sin filtro de rango**.
 *
 * 🔴 Esto no es un adorno: sin ello la pantalla abre en el mes en curso y dice «Resumen sobre 20
 * de 20 fichas» —cierto y completo dentro del rango— sin ninguna pista de que existen 4 fichas del
 * 29-ago que son las únicas de Makro y traen las góndolas más anchas del estudio.
 *
 * Un total honesto dentro de un rango que el usuario no eligió es la forma más fácil de esconder
 * media muestra. Con esto la pantalla puede decir «hay N fichas fuera del rango».
 */
export async function bordesDelEstudio(
  tabla: 'mer_fichas' | 'pg_fichas',
): Promise<{ primera: string | null; ultima: string | null; total: number }> {
  const { data, error, count } = await supabase
    .from(tabla).select('fecha', { count: 'exact' })
    .order('fecha', { ascending: true }).limit(1);
  if (error) throw new PgApiError(error.message, error.code ?? null);
  const primera = data?.[0]?.fecha as string | undefined;

  // `nullsFirst: false`: en orden descendente Postgres pone los NULL PRIMERO, y una sola ficha sin
  // fecha haría que el estudio dijera «va del … al —» (30-set, igual que en api/yichang.ts).
  const { data: d2, error: e2 } = await supabase
    .from(tabla).select('fecha').order('fecha', { ascending: false, nullsFirst: false }).limit(1);
  if (e2) throw new PgApiError(e2.message, e2.code ?? null);

  return {
    primera: primera ?? null,
    ultima: (d2?.[0]?.fecha as string | undefined) ?? null,
    total: count ?? 0,
  };
}
